import React, { useEffect, useState, useMemo } from 'react';
import {
  MaterialReactTable,
  useMaterialReactTable,
  MRT_ColumnDef,
  type MRT_SortingState,
} from 'material-react-table';

import { AppBar, Toolbar, Button, FormControl, InputLabel, MenuItem, Select, useTheme, Box, Typography, useMediaQuery, Paper } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { SelectChangeEvent } from '@mui/material/Select';
import { useParams } from 'react-router-dom';
import SiteMenu from './components/SiteMenu';
const apiUrl = process.env.REACT_APP_API_URL;


interface RaceInfo {
  raceID: string;
  raceName: string;
  eventDate: string;
}
interface Participant {
  id: string;
  name: string;
  bib?: string;
  gender?: string;
  genderId?: string;
  status?: string;
  division?: string;
  divisionId?: string;
}

interface Result extends Participant {
  participantId: string;
  eventId: string; // New property to link to the event
  overallRank: number;
  catRank: number;
  finishTime: string;
  genderRank: number;
  // Add more properties as needed
}

interface Gender {
  genderName: string;
  entered: string;
  finished: string;
  totalEventGenderEntered: string;
  totalEventGenderFinished: string;
  totalFinished: string;
}

interface Division {
  divisionName: string;
  divisionId: string;
  genders: Record<string, Gender>;
  totalEventFinished: string;
  totalEventEntered: string;
}



interface Split {
  splitId: string;
  participantId: string;
  eventId: string; // New property to link to the event
  timingPoint: string;
  totalTime: string;
  // Define other properties based on your splits structure
}

interface Events {
  [key: string]: Event;
}
interface Event {
  eventName: string;
  eventDate: string;
  totalFinishers: string;
  divisions: Record<string, Division>;
  splits: Split[];
}

interface PaginationState {
  pageIndex: number;
  pageSize: number;
}


const RaceResults = () => {
  const [data, setData] = useState<Result[]>([]);
  const [events, setEvents] = useState<Events>({}); 
  const [selectedEventId, setSelectedEventId] = useState<string | null>("");
  const [selectedDivisionId, setSelectedDivisionId] = useState<string | null>(null);
  const [selectedResultView, setSelectedResultView] = useState<string>('Overall');
  const [selectedGender, setSelectedGender] = useState<string | null>(null);
  const [columnOrder, setColumnOrder] = useState<string[]>([])
  const [raceInfo, setRaceInfo] = useState<RaceInfo | null>(null);
  const [logoURL, setLogoURL] = useState("");
  const [tableHeight, setTableHeight] = useState<number>(0);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20, // Adjust based on your default page size
  });
  const [sorting, setSorting] = useState<MRT_SortingState>([{
    id: 'overallRank', desc: false
  }]);

  const tableInterfaceRef = React.useRef<HTMLDivElement>(null);
  const tableButtonsRef = React.useRef<HTMLDivElement>(null);

  const raceDir = useParams();
 
  const theme = useTheme();

  const matches = useMediaQuery(theme.breakpoints.up('md'));

  // const onPaginationChange = (paginationChange: { pageIndex: number; pageSize: number }) => {
  //   setPagination({
  //     pageIndex: paginationChange.pageIndex,
  //     pageSize: paginationChange.pageSize,
  //   });
  // };

  useEffect(() => {

    const resultsSource = new EventSource(`${apiUrl}/api/live?raceDir=` + raceDir.raceDir);
    resultsSource.onmessage = (event) => {
      const parsedData = JSON.parse(event.data);
      setRaceInfo(parsedData.race);
      
      const processedResults: Result[] = parsedData.results
        .filter((result: any) => {
          const participant = parsedData.participants.find((p: Participant) => p.id === result.participantId);
          //return participant?.status !== 'Not started'; // Filter out 'Not Started'
          return participant;
        })
        .map((result: any) => {
          const participant = parsedData.participants.find((p: Participant) => p.id === result.participantId);
          // Set finishTime to 'DNS' if status is 'DNS', else keep original finishTime
          let finishTime;
          let overallRank;
          if (participant?.status === 'DNS') {
            finishTime = 'DNS';
            overallRank = 'DNS';
          } else if (participant?.status === 'Not started') {
            finishTime = 'Not Started';
            overallRank = 'Not Started';
          } else {
            finishTime = result.finishTime.slice(0, -4);
            overallRank = result.overallRank;
          }

          return { 
            ...result, 
            name: participant ? participant.name : 'Unknown',
            bib: participant ? participant.bib : '',
            gender: participant ? participant.gender : '',
            genderId: participant ? participant.genderId : '',
            status: participant ? participant.status : '',
            division: participant ? participant.division : '',
            divisionId: participant ? participant.divisionId : '',
            finishTime, // Adjusted finishTime
            overallRank, // Adjusted overallRank
          };
        });
      setLogoURL(`${apiUrl}/result-uploads/${raceDir.raceDir}/logo.png`);
      setData(processedResults || []);
      setEvents(parsedData.events || {});
      if (!selectedEventId && Object.keys(parsedData.events).length > 0) {
        const firstEventId = Object.keys(parsedData.events)[0];
        setSelectedEventId(firstEventId);
      }

    }
    return () => {
      resultsSource.close();
    };
  }, [raceDir.raceDir, selectedEventId]);
  
  const customSort = (rowA: any, rowB: any) => {
    const rankA = rowA.original.overallRank;
    const rankB = rowB.original.overallRank;
  
    // Handling non-numeric specific orders
    const order: { [key: string]: number } = { 'DNS': 1, 'Not Started': 2 };
  
    const getSortValue = (rank: number) => {
      if (!isNaN(Number(rank))) return { type: 'number', value: Number(rank) };
      return { type: 'string', value: order[rank] || 3 }; // Default to 3 for any other non-numeric values
    };
  
    const sortValueA = getSortValue(rankA);
    const sortValueB = getSortValue(rankB);
  
    // First, sort by type to ensure numbers come before strings
    if (sortValueA.type !== sortValueB.type) {
      return sortValueA.type === 'number' ? -1 : 1;
    }
  
    // Then, if both are numbers or both are strings, sort by their value
    return sortValueA.value - sortValueB.value;
  };


  const columns: MRT_ColumnDef<Result>[] = useMemo(() => {
    switch (selectedResultView) {
      case 'Overall': {
        const cols = [
          { accessorKey: 'overallRank', header: 'Rank', minSize: 85, size: 90, grow: false, sortingFn: (rowA: any, rowB: any) => customSort(rowA, rowB), muiTableBodyCellProps: { className: 'tabular-nums' } },
          { accessorKey: 'bib', header: 'Bib', size: 80, minSize: 80, grow: false },
          { accessorKey: 'name', header: 'Name', minSize: 150 },
          { accessorKey: 'division', header: 'Category', size: 130, minSize: 110, grow: false },
          { accessorKey: 'gender', header: 'Gender', size: 100, minSize: 100, grow: false },
          { accessorKey: 'finishTime', header: 'Finish Time', size: 140, minSize: 140, grow: false, muiTableBodyCellProps: { className: 'tabular-nums' } },
        ];
        setColumnOrder(['mrt-row-expand', ...cols.map((c) => c.accessorKey)]);
        return cols;
      }
      case 'Gender Results': {
        const cols = [
          { accessorKey: 'genderRank', header: 'Gen Rank', minSize: 85, size: 90, grow: false, sortingFn: (rowA: any, rowB: any) => customSort(rowA, rowB), muiTableBodyCellProps: { className: 'tabular-nums' } },
          { accessorKey: 'bib', header: 'Bib', size: 80, minSize: 80, grow: false },
          { accessorKey: 'name', header: 'Name', minSize: 150 },
          { accessorKey: 'gender', header: 'Gender', size: 100, minSize: 100, grow: false },
          { accessorKey: 'finishTime', header: 'Finish Time', size: 140, minSize: 140, grow: false, muiTableBodyCellProps: { className: 'tabular-nums' } },
        ];
        setColumnOrder(['mrt-row-expand', ...cols.map((c) => c.accessorKey)]);
        return cols;
      }
      case 'Category Results': {
        const cols = [
          { accessorKey: 'catRank', header: 'Cat Rank', minSize: 85, size: 90, grow: false, sortingFn: (rowA: any, rowB: any) => customSort(rowA, rowB), muiTableBodyCellProps: { className: 'tabular-nums' } },
          { accessorKey: 'bib', header: 'Bib', size: 80, minSize: 80, grow: false },
          { accessorKey: 'name', header: 'Name', minSize: 150 },
          { accessorKey: 'division', header: 'Category', size: 130, minSize: 110, grow: false },
          { accessorKey: 'finishTime', header: 'Finish Time', size: 140, minSize: 140, grow: false, muiTableBodyCellProps: { className: 'tabular-nums' } },
        ];
        setColumnOrder(['mrt-row-expand', ...cols.map((c) => c.accessorKey)]);
        return cols;
      }
      default:
        return [];
    }
  }, [selectedResultView]);

  const handleEventSelection = (eventId: string) => {
    setSelectedEventId(eventId);
    setSelectedDivisionId(null); // Reset division selection when changing events
  };

  const handleDivisionSelection = (event: SelectChangeEvent<string>) => {
    setSelectedDivisionId(event.target.value);
  };

  const handleResultViewSelection = (view: string) => {
    setSelectedResultView(view);
    if(view === 'Category Results'){
      setSorting([{id: 'catRank', desc: false }]);
    } else if (view === 'Gender Results'){
      setSorting([{ id: 'genderRank', desc: false }])
    } else {
      setSorting([{ id: 'overallRank', desc: false }])
    }
    if(view !== 'Category Results') {
      setSelectedDivisionId(null);
    }
    if(view !== 'Gender Results') {
      setSelectedGender(null);
    }
  }
  // Handler for gender selection
  const handleGenderSelection = (event: SelectChangeEvent<string>) => {
    setSelectedGender(event.target.value);
  };

  // Generating unique gender options from the data
  const genderOptions = useMemo(() => {
    const allGenders = new Set(data.map(result => result.gender));
    return Array.from(allGenders);
  }, [data]);

  const filteredData = useMemo(() => {
    let filteredResults = data;
    if (selectedEventId) {
      filteredResults = filteredResults.filter(result => result.eventId === selectedEventId);
    }
    if (selectedDivisionId && selectedEventId) {
      filteredResults = filteredResults.filter(result => result.division === events[selectedEventId]?.divisions[selectedDivisionId]?.divisionName);
      filteredResults = filteredResults.filter(result => selectedGender === 'All' || '' ? result.gender : result.gender === selectedGender);
    }
    if (selectedEventId && selectedGender) {
      filteredResults = filteredResults.filter(result => selectedGender === 'All' || '' ? result.gender : result.gender === selectedGender);
    }
    return filteredResults;
  }, [data, selectedEventId, selectedDivisionId, selectedGender, events]);
  

  const table = useMaterialReactTable<Result>({
    columns,
    data: filteredData,
    enableExpandAll: true,
    enableExpanding: true,
    enableRowSelection: false,
    enableStickyHeader: true,
    enablePagination: true,
    enableRowVirtualization: false, // Disable pagination for virtualization
    muiTableContainerProps: { sx: { maxHeight: '10000px' }},
    autoResetPageIndex: false,
    onSortingChange: setSorting,
    initialState: {
      density: 'compact',
    },
    muiPaginationProps: {
      color: 'primary',
      shape: 'rounded',
      variant: 'text',
      size: 'small',
      siblingCount: 1
    },
    paginationDisplayMode: 'pages',
    onPaginationChange: setPagination,
    state: {
      columnOrder,
      pagination,
      sorting
    },

    muiExpandButtonProps: ({ row, table }) => ({
      onClick: () => {
        table.setExpanded({ [row.id]: !row.getIsExpanded() })
      }, //only 1 detail panel open at a time
      sx: {
        transform: row.getIsExpanded() ? 'rotate(180deg)' : 'rotate(-90deg)',
        transition: 'transform 0.2s',
      },
    }),
    renderDetailPanel: ({ row }) =>
      row.original.overallRank ? (
        <Paper 
        elevation = {3}  
        sx = {{
          paddingLeft: theme.spacing(2),
          paddingRight: theme.spacing(2),
          paddingTop: theme.spacing(1),
          paddingBottom: theme.spacing(1),
          // Dark mode: a translucent wash of the brand accent instead of a
          // plain neutral grey, so it reads as part of the palette, not a patch.
          backgroundColor: theme.palette.mode === 'light' ? theme.palette.primary.main : alpha(theme.palette.primary.main, 0.14)
        }}>
          <Box
          sx={{
            display: 'grid',
            margin: 'auto',
            gridTemplateColumns: '1fr',
            width: '100%',
          }}
          >
            <Typography variant="body2">Overall Rank: {row.original.overallRank} / {row.original.divisionId ? events[row.original.eventId]?.divisions[row.original.divisionId]?.totalEventFinished : null}</Typography>
            <Typography variant="body2">Gender Rank: {row.original.genderRank} / {(row.original.divisionId && row.original.genderId) ? events[row.original.eventId]?.divisions[row.original.divisionId]?.genders[row.original.genderId].totalEventGenderFinished : null}</Typography>
            <Typography variant="body2">Category Rank: {row.original.catRank} / {(row.original.divisionId && row.original.genderId) ? events[row.original.eventId]?.divisions[row.original.divisionId]?.genders[row.original.genderId].finished : null}</Typography>
          </Box>
        </Paper> 
      ) : null,
    
    displayColumnDefOptions: {
      'mrt-row-expand': {
        size: 50, //adjust the size of the row select column
        grow: false, //new in v2.8 (default is false for this column)
      },
    },


    muiTableHeadCellProps: { sx: { fontWeight: 'bold', wordBreak: 'normal', whiteSpace: 'normal' } }, // Set the font weight for the table head cells
    muiTableBodyRowProps: ({ row }) => {
      return { 
        onClick: (event) =>{
          table.setExpanded({ [row.id]: !row.getIsExpanded() })
        },
        sx: {
          ...((row.original.overallRank <= 3 && row.original.overallRank >=1 && selectedResultView === 'Overall' ) ||
          (row.original.catRank <= 3 && row.original.overallRank >=1 && selectedResultView ==="Category Results") ||
          (row.original.genderRank <= 3 && row.original.overallRank >=1 && selectedResultView ==="Gender Results") ? {
              //color: '#1d1d1d',
              // Dark mode: a crisp Kiwi Green wash, not a muddy darkened Slate.
              backgroundColor: theme.palette.mode === 'light' ? theme.palette.primary.light : alpha(theme.palette.primary.main, 0.22)
            } : {}),
          cursor: 'pointer',  
        },
      };
    },
    muiTablePaperProps: ({ table }) => ({
      style: {
        zIndex: table.getState().isFullScreen ? "1200" : undefined,
      },
    }),
    muiTableBodyCellProps: ({cell}) => {
      return { 
        sx: {
          ...((cell.row.original.overallRank <= 3 && cell.row.original.overallRank >=1 && selectedResultView === 'Overall' ) ||
          (cell.row.original.catRank <= 3 && cell.row.original.overallRank >=1 && selectedResultView ==="Category Results") ||
          (cell.row.original.genderRank <= 3 && cell.row.original.overallRank >=1 && selectedResultView ==="Gender Results") ? {
              //color: '#1d1d1d', // Your font color
            } : {}),
          },
        };
    },
});
  
  const divisionOptions = selectedEventId ? Object.values(events[selectedEventId]?.divisions || {}) : [];
  //const selectedEventName = selectedEventId ? events[selectedEventId]?.eventName : '';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <AppBar position="static">
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <img src={logoURL} alt="Event Logo" style={{ height: '100px', maxHeight: '100px', marginRight: theme.spacing(2), padding: theme.spacing(2) }} />
            <Typography variant="h6">
              {raceInfo && raceInfo.raceName}
            </Typography>
          </Box>
          <SiteMenu />
        </Toolbar>
      </AppBar>
      <Paper elevation={5} 
        component="main"
        ref={tableInterfaceRef}
        sx={{
          display: 'flex',
          flexGrow: 1,
          flexDirection: 'column',
          marginTop: theme.spacing(2), // Adjusts the outside margin
          marginBottom: {
            xs: theme.spacing(8),
            sm: theme.spacing(2),
            md: theme.spacing(2)
          },
          marginRight: theme.spacing(2),
          marginLeft: theme.spacing(2),
          padding: theme.spacing(1), // Adds padding inside the box
          borderRadius: '10px', // Optional: adds rounded corners
          backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[200] : theme.palette.background.paper,
          height: {
            // xs: `calc(100% - 176px)`, // Smaller devices might have more elements visible
            // md: `calc(100% - 176px)`, // Adjusted value for larger devices
          }
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', padding: theme.spacing(1) }}>
          <div>
            {/* Flex container for buttons and dropdown */}
            <div ref={tableButtonsRef} style={{ display: 'flex', flexDirection: 'column', alignItems: matches ? 'flex-start' : 'flex-end' }}>
              {/* Button container with flexbox to align buttons */}
              { matches ? (
                <div style = {{width: '100%'}}>
                  <div style = {{display: 'flex', flexDirection: 'row', width: '100%'}}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: theme.spacing(1) }}>
                      {Object.entries(events).map(([eventId, eventDetails]) => (
                        <Button 
                          key={eventId} 
                          variant="contained"
                          color={selectedEventId === eventId ? 'primary' : "secondary"}
                          style={{ marginBottom: '0.5rem' }} // Adjust as necessary
                          onClick={() => handleEventSelection(eventId)}
                        >
                          {eventDetails.eventName}
                        </Button>
                      ))}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', marginLeft: 'auto'}}>
                      <Typography variant = 'h6' >{selectedEventId ? events[selectedEventId].eventName + " "+ selectedResultView : ""}</Typography>
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '1rem', gap: theme.spacing(1) }}>
                    <Button onClick={() => handleResultViewSelection('Overall')} variant = 'contained' color={selectedResultView === 'Overall' ? 'primary' : 'secondary'} size="small">Overall</Button>
                    <Button onClick={() => handleResultViewSelection('Gender Results')} variant = 'contained' color={selectedResultView === 'Gender Results' ? 'primary' : 'secondary' } size="small">Gender Results</Button>
                    <Button onClick={() => handleResultViewSelection('Category Results')} variant = 'contained' color={selectedResultView === 'Category Results' ? 'primary' : 'secondary' } size="small">Category Results</Button>         
                  </div>
                </div>
              ) : (
                <div style={{ width: '100%', display: 'flex', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                  <div style={{ width: '48%', marginBottom: theme.spacing(0) }}>
                    {/* Event Dropdown */}
                    <FormControl fullWidth size="small" margin="dense">
                      <InputLabel>Event</InputLabel>
                      <Select
                        value={selectedEventId}
                        label="Event"
                        onChange={(e) => {
                          e.target.value ? handleEventSelection(e.target.value) : setSelectedEventId(null);
                          setSelectedDivisionId(null);
                        }}
                      >
                        {Object.entries(events).map(([eventId, eventDetails]) => (
                          <MenuItem key={eventId} value={eventId}>
                            {eventDetails.eventName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                  <div style={{ width: '48%', marginBottom: theme.spacing(0) }}>
                    {/* Results View Dropdown */}
                    <FormControl fullWidth size="small" margin="dense">
                      <InputLabel>Results View</InputLabel>
                      <Select
                        value={selectedResultView}
                        label="Results View"
                        onChange={(e) => handleResultViewSelection(e.target.value)}
                      >
                        <MenuItem value="Overall">Overall</MenuItem>
                        <MenuItem value="Gender Results">Gender Results</MenuItem>
                        <MenuItem value="Category Results">Category Results</MenuItem>
                      </Select>
                    </FormControl>
                  </div>
                </div>
              )}
              {/* Dropdown container to control its width */}
              {selectedEventId && selectedResultView === 'Category Results' && (
                <div className={matches ? "desktop-dropdowns" : "mobile-dropdowns"}>
                  <div style={{ width: '48%', marginBottom: theme.spacing(0) }}>
                    <FormControl fullWidth size="small" margin="dense">
                      <InputLabel id="gender-select-label">Gender</InputLabel>
                      <Select
                        labelId="gender-select-label"
                        id="gender-select"
                        value={selectedGender || ''}
                        onChange={handleGenderSelection}
                        label="Gender"
                      >
                        <MenuItem value="All">
                          <em>All</em>
                        </MenuItem>
                        {genderOptions.map(gender => (
                          <MenuItem key={gender} value={gender}>{gender}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                  <div style={{ width: '48%', marginBottom: theme.spacing(0) }}>
                    <FormControl fullWidth size="small" margin="dense" sx = {{ marginBottom: theme.spacing(0) }}>
                      <InputLabel id="division-select-label">Category</InputLabel>
                      <Select
                        value={selectedDivisionId || ''}
                        onChange={handleDivisionSelection}
                        label="Category"
                      >
                        {divisionOptions.map(division => (
                          <MenuItem key={division.divisionId} value={division.divisionId}>{division.divisionName}</MenuItem>
                        ))}
                      </Select> 
                    </FormControl>
                  </div>
                </div>
              )}
              {selectedEventId && selectedResultView === 'Gender Results' && (
                <div className={matches ? "desktop-dropdowns" : "mobile-dropdowns"}>
                  <div style={{ width: '48%', marginBottom: theme.spacing(0) }}>
                    <FormControl fullWidth size="small" margin='dense' sx={{ marginBottom: theme.spacing(0) }}>
                      <InputLabel id="gender-select-label">Gender</InputLabel>
                      <Select
                        labelId="gender-select-label"
                        id="gender-select"
                        value={selectedGender || ''}
                        onChange={handleGenderSelection}
                        label="Gender"
                      >
                        <MenuItem value="All">
                          <em>All</em>
                        </MenuItem>
                        {genderOptions.map(gender => (
                          <MenuItem key={gender} value={gender}>{gender}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                </div>
              )}
            </div>
          </div>
          <Box sx={{ flexGrow: 1, overflowY: 'auto', mt: theme.spacing(1) }}>
            <MaterialReactTable table={table} />
          </Box>
        </div>
      </Paper>
    </Box>
  );
  
};

export default RaceResults;
